/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

import liveSearchFacetsBh from "./bh.js";
import liveSearchFacetsMapping from "./mapping.js";
import liveSearchFacetsOptions from "./options.js";
import liveSearchFacetsReset from "./reset.js";
import liveSearchFacetsState from "./state.js";

const liveSearchFacets = {
    ...liveSearchFacetsBh,
    ...liveSearchFacetsMapping,
    ...liveSearchFacetsOptions,
    ...liveSearchFacetsReset,
    ...liveSearchFacetsState,
    /**
     * Update facets based on the fetched data
     *
     * @param {Array} facets - The array of fetched facets.
     */
    updateFacets(facets) {
        if (facets.length) {
            this.facets = this.handleFacets(
                this.processArray(
                    facets,
                    facet => this.facetConfig.bhAttributes.includes(facet.attribute),
                    this.facetConfig.mergedBhLabel,
                    this.facetConfig.mergedBhAttribute
                )
            );
            // filter facets with empty options
            this.facets = this.facets.filter(facet => facet.options);
        }
    },
    /**
     * Sort numeric facet buckets by title
     *
     * @param {Array} facets - The array of facets to process.
     * @param {string} attribute - The attribute of the facet to sort.
     */
    sortNumericFacetBuckets(facets, attribute) {
        const facet = facets.find(f => f.attribute === attribute);
        if (facet && Array.isArray(facet.buckets)) {
            facet.buckets = facet.buckets.slice().sort((a, b) => Number(a.title) - Number(b.title));
        }
    },
    /**
     * Sort buckets of a facet by a custom order defined in an array
     *
     * @param facets
     * @param attribute
     * @param orderList
     */
    sortBucketsByCustomOrder(facets, attribute, orderList) {
        const facet = facets.find(f => f.attribute === attribute);
        if (!facet || !Array.isArray(facet.buckets)) return;

        facet.buckets = facet.buckets.slice().sort((a, b) => {
            const indexA = orderList.indexOf(a.title.trim());
            const indexB = orderList.indexOf(b.title.trim());
            return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
        });
    },
    /**
     * Disable facets that are not available
     *
     * @param {Array} facets - The array of facets to process.
     * @returns {Array} - The updated array of facets with disabled options.
     */
    handleFacets(facets) {
        // Check if facets are already mapped and update options with disabled state
        if (this.facets.length) {
            return this.facets.map(facet => this.updateFacetOptions(facet, facets));
        }
        // If facets are not already mapped, map them
        return this.mapFacets(facets);
    },
    /**
     * Find the current facet from the facets array
     *
     * @param {Object} facet - The facet to find.
     * @param {Array} facets - The array of facets to search.
     * @returns {Object} - The found current facet.
     */
    findCurrentFacet(facet, facets) {
        return facets.find(f => f.attribute === facet.attribute);
    },
    /**
     * Handle facet click
     * used in facets-selected.phtml && facets-items.phtml
     *
     * @param $event
     * @param {string} attribute
     * @param {string} option
     */
    handleFacetClick($event, attribute, option) {
        const facetIndex = this.selectedFacets.findIndex(facet => facet.attribute === attribute && facet.option === option);

        if (facetIndex !== -1) {
            // Remove facet
            this.selectedFacets.splice(facetIndex, 1);
            this.removeFacetFocus($event);
        } else {
            // Add facet
            this.selectedFacets.push({ attribute, option });
        }

        this.resetProducts();
        this.getProducts("facets");

        if (!this.isMobile && this.facetConfig.singleButtonAttributes.includes(attribute)) {
            this.$dispatch("facets-expanded-close-all");
        }
        this.$dispatch("facets-changed", attribute);
    },
    /**
     * Remove focus from facet button
     * Workaround for deselected options highlighted as selected on mobile devices
     *
     * @param event
     */
    removeFacetFocus(event) {
        if (this.isMobile) {
            setTimeout(() => event.target.blur(), 300);
        }
    },
    /**
     * Get active facets count for the given attribute
     *
     * @param {string} attribute
     * @returns {number}
     */
    getActiveFacetsCount(attribute) {
        const selectedFacets = this.selectedFacets;
        const isMergedBhAttribute = attribute === this.facetConfig.mergedBhAttribute;
        return selectedFacets.filter(facet =>
            isMergedBhAttribute
                ? this.facetConfig.bhAttributes.includes(facet.attribute)
                : facet.attribute === attribute || facet.attribute.includes(attribute)
        ).length;
    },

    /**
     * Checks if a facet should be hidden from the list of facets.
     *
     * A facet should be hidden if it has only one option and that option is "no"
     *
     * @param {Object} facet - The facet to check.
     * @returns {boolean} - True if the facet should be hidden, false otherwise.
     */
    shouldHideFacet(facet) {
        // Hide if options are missing or empty
        if (!facet.options || facet.options.length === 0) {
            return true;
        }
        // Hide if only one option and its id is "no"
        return facet.options.length === 1 && facet.options[0].id === "no";
    },
}

export default liveSearchFacets;
